import React, { useState } from 'react';

import { Button, ButtonGroup, Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@@/ui/primitives';
import { BarChartIcon, RefreshCwIcon } from 'lucide-react';
import { FlowLogEvent } from '../../types/flow-log';
import { useLambdaMetrics } from '../../hooks/use-lambda-metrics';
import { cn } from '@@/lib/utils';
import { format } from 'date-fns';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Spinner } from '@@/ui/components';

interface LambdaMetricsContentProps {
  raw: FlowLogEvent;
  functionName: string;
}

type TimeRange = 'invocation' | '1h' | '3h' | '12h' | '1d' | '3d' | '1w';

interface TimeRangeOption {
  value: TimeRange;
  label: string;
  milliseconds?: number;
}

function MetricLineChart(props: { title: string; data: Array<{ timestamp: string; value: number }>; color: string }) {
  const values = props.data.map((d) => d.value);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);
  const sum = values.reduce((acc, val) => acc + val, 0);
  const avgValue = sum > 0 ? sum / values.length : 0;
  const domain = [0, maxValue * 1.2];

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center gap-2'>
        <div className='text-sm font-medium'>{props.title}</div>
        <div className='text-xs text-neutral-500 flex items-center gap-2'>
          <span>Avg: {avgValue.toFixed(2)}</span>
          <span>•</span>
          <span>Max: {maxValue.toFixed(2)}</span>
          <span>•</span>
          <span>Min: {minValue.toFixed(2)}</span>
        </div>
      </div>
      <div className='h-48 w-full'>
        <ResponsiveContainer width='100%' height='100%'>
          <LineChart data={props.data} margin={{ left: -10 }}>
            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
            <XAxis
              dataKey='timestamp'
              tickFormatter={(value) => format(new Date(value), 'HH:mm')}
              tick={{ fontSize: 11 }}
              minTickGap={50}
            />
            <YAxis tick={{ fontSize: 11 }} domain={domain} width={40} />
            <Tooltip labelFormatter={(value) => format(new Date(value), 'MMM d, HH:mm:ss')} />
            <Line
              isAnimationActive={false}
              type='linear'
              dataKey='value'
              stroke={props.color}
              activeDot={false}
              strokeWidth={2}
              dot={{ r: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function MetricBarChart(props: { title: string; data: Array<{ timestamp: string; value: number }>; color: string }) {
  const values = props.data.map((d) => d.value);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);
  const sum = values.reduce((acc, val) => acc + val, 0);
  const avgValue = sum / values.length;
  const domain = [0, maxValue * 1.2];
  const barSize = props.data.length > 50 ? 4 : undefined;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center gap-2'>
        <div className='text-sm font-medium'>{props.title}</div>
        <div className='text-xs text-neutral-500 flex items-center gap-2'>
          <span>Avg: {avgValue.toFixed(2)}</span>
          <span>•</span>
          <span>Max: {maxValue.toFixed(2)}</span>
          <span>•</span>
          <span>Min: {minValue.toFixed(2)}</span>
        </div>
      </div>
      <div className='h-48 w-full'>
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart data={props.data} margin={{ left: -10 }}>
            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
            <XAxis
              dataKey='timestamp'
              tickFormatter={(value) => format(new Date(value), 'HH:mm')}
              tick={{ fontSize: 11 }}
              minTickGap={50}
            />
            <YAxis tick={{ fontSize: 11 }} domain={domain} width={40} />
            <Tooltip labelFormatter={(value) => format(new Date(value), 'MMM d, HH:mm:ss')} />
            <Bar isAnimationActive={false} dataKey='value' fill={props.color} maxBarSize={barSize} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function LambdaMetricsContent(props: LambdaMetricsContentProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('invocation');
  const [now, setNow] = useState(() => Date.now());

  const timestamp = new Date(props.raw.Timestamp).getTime();

  const timeRangeOptions: TimeRangeOption[] = [
    { value: 'invocation', label: 'During Invocation' },
    { value: '1h', label: '1h', milliseconds: 1 * 60 * 60 * 1000 },
    { value: '3h', label: '3h', milliseconds: 3 * 60 * 60 * 1000 },
    { value: '12h', label: '12h', milliseconds: 12 * 60 * 60 * 1000 },
    { value: '1d', label: '1d', milliseconds: 24 * 60 * 60 * 1000 },
    { value: '3d', label: '3d', milliseconds: 3 * 24 * 60 * 60 * 1000 },
    { value: '1w', label: '1w', milliseconds: 7 * 24 * 60 * 60 * 1000 }
  ];

  let startTime: string;
  let endTime: string;

  if (timeRange === 'invocation') {
    startTime = new Date(timestamp - 1.5 * 60 * 60 * 1000).toISOString();
    endTime = new Date(timestamp + 1.5 * 60 * 60 * 1000).toISOString();
  } else {
    const option = timeRangeOptions.find((opt) => opt.value === timeRange);
    const ms = option?.milliseconds ?? 3 * 60 * 60 * 1000;
    startTime = new Date(now - ms).toISOString();
    endTime = new Date(now).toISOString();
  }

  const {
    data: metricsData,
    isLoading,
    isRefetching,
    error,
    refetch
  } = useLambdaMetrics({
    functionName: props.functionName,
    startTime,
    endTime
  });

  const handleRefresh = () => {
    setNow(Date.now());
    refetch();
  };

  const formatTimestamp = (isoString: string) => {
    return format(new Date(isoString), "MMM d, yyyy 'at' h:mm:ss a");
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <div className='flex flex-col gap-1'>
          <div className='flex items-center justify-between'>
            <div className='text-sm font-medium'>Lambda Function</div>
          </div>
          <div className='text-xs text-neutral-600 break-all'>{props.functionName}</div>
        </div>
        <div className='flex items-center gap-2'></div>
      </div>

      <div className='flex flex-col gap-2'>
        <div className='flex flex-row gap-2 justify-between items-center'>
          <ButtonGroup className='text-xs!'>
            {timeRangeOptions.map((option) => (
              <Button
                key={option.value}
                variant='outline'
                size='sm'
                onClick={() => setTimeRange(option.value)}
                className={cn('font-[450]!', timeRange === option.value ? 'bg-neutral-100' : '')}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>

          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={handleRefresh}
              disabled={isLoading || isRefetching}
              className='text-xs! font-medium!'
            >
              <RefreshCwIcon className={cn('size-3.5', isRefetching ? 'animate-spin' : '')} />
              Refresh
            </Button>
          </div>
        </div>
        <div className='text-xs text-neutral-500 flex items-center gap-2'>
          <span>
            <span className='font-medium'>From:</span> {formatTimestamp(startTime)}
          </span>
          <span>•</span>
          <span>
            <span className='font-medium'>To:</span> {formatTimestamp(endTime)}
          </span>
        </div>
      </div>

      {error && <div className='p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-900'>{error}</div>}

      {isLoading && (
        <div className='flex items-center justify-center py-8'>
          <Spinner className='size-5 text-neutral-400' />
        </div>
      )}

      {metricsData && !isLoading && (
        <div className='flex flex-col gap-6'>
          <MetricBarChart title='Invocations' data={metricsData.data.invocations} color='#3b82f6' />
          <MetricBarChart title='Errors' data={metricsData.data.errors} color='#ef4444' />
          <MetricLineChart title='Duration (ms)' data={metricsData.data.duration} color='#8b5cf6' />
          <MetricLineChart title='Concurrent Executions' data={metricsData.data.concurrentExecutions} color='#10b981' />
        </div>
      )}
    </div>
  );
}

export function LambdaMetricsSheet(props: { raw: FlowLogEvent }) {
  const [open, setOpen] = useState(false);

  const lambdaArn = props.raw.Parameters?.FunctionArn;
  const functionName = typeof lambdaArn === 'string' ? lambdaArn.split(':').pop() : null;

  if (!functionName) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant='outline' className='text-xs! h-min px-2! rounded-full font-medium! py-0.5 gap-1'>
          <BarChartIcon className='size-3.5' />
          Metrics
        </Button>
      </SheetTrigger>
      <SheetContent className='overflow-auto min-w-2xl! font-sans'>
        <SheetHeader className='border-b border-neutral-200 bg-neutral-50'>
          <SheetTitle>Lambda Metrics</SheetTitle>
        </SheetHeader>
        <div className='px-4'>
          <LambdaMetricsContent raw={props.raw} functionName={functionName} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
